'use client';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/lib/i18n/language-provider';
import { useRouter } from 'next/navigation';
import { ScrollReveal } from '@/components/scroll-reveal';
import {
  Crown,
  Clock,
  RefreshCw,
  Sparkles,
  Check,
  Zap,
  Shield,
  Star,
  ArrowRight,
} from 'lucide-react';

interface DemoEndedOverlayProps {
  canRestartDemo: boolean;
  cooldownRemaining: number;
  onRestartDemo: () => void;
}

export function DemoEndedOverlay({
  canRestartDemo,
  cooldownRemaining,
  onRestartDemo,
}: DemoEndedOverlayProps) {
  const { t } = useLanguage();
  const router = useRouter();

  const formatCooldown = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins} minutes`;
  };

  const proFeatures = [
    {
      icon: <Zap className="h-5 w-5" />,
      title: t('demo.ended.feature1.title') || 'Éléments illimités',
      desc: t('demo.ended.feature1.desc') || 'Ajoutez autant de processeurs, panneaux et armoires que nécessaire',
    },
    {
      icon: <Shield className="h-5 w-5" />,
      title: t('demo.ended.feature2.title') || 'Sauvegarde cloud',
      desc: t('demo.ended.feature2.desc') || 'Vos projets sont sauvegardés automatiquement et accessibles partout',
    },
    {
      icon: <Star className="h-5 w-5" />,
      title: t('demo.ended.feature3.title') || 'Export professionnel',
      desc: t('demo.ended.feature3.desc') || 'Exportez en PDF, PNG haute résolution pour vos clients',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm">
      <ScrollReveal animation="zoom-blur" duration={0.8}>
        <div className="relative w-full max-w-2xl">
          {/* Glow effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 blur-2xl opacity-60" />

          {/* Main card */}
          <div className="relative border-2 border-foreground/10 bg-background/95 backdrop-blur-md rounded-2xl p-8 shadow-2xl">
            {/* Badge */}
            <div className="flex justify-center mb-6">
              <Badge variant="secondary" className="bg-orange-500/10 text-orange-500 border-orange-500/30 px-4 py-1">
                <Clock className="h-4 w-4 mr-2" />
                {t('demo.ended.badge') || 'Session terminée'}
              </Badge>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-3">
                {t('demo.ended.title') || 'Votre démonstration est terminée'}
              </h2>
              <p className="text-lg text-muted-foreground">
                {t('demo.ended.subtitle') || "Vous avez pu découvrir les fonctionnalités du Synoptique. Passez en Pro pour un accès complet !"}
              </p>
            </div>

            {/* Pro features */}
            <div className="space-y-4 mb-8">
              {proFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold">{feature.title}</h4>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="space-y-3">
              {/* Primary CTA - Upgrade */}
              <Button
                size="lg"
                className="w-full h-14 text-lg bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white shadow-lg"
                onClick={() => router.push('/pro')}
              >
                <Crown className="mr-3 h-6 w-6" />
                {t('demo.ended.upgradeButton') || 'Passer en Pro - 9,99€/6 mois'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              {/* Secondary CTA - Restart demo or cooldown */}
              {canRestartDemo ? (
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full h-12"
                  onClick={onRestartDemo}
                >
                  <RefreshCw className="mr-2 h-5 w-5" />
                  {t('demo.ended.restartButton') || 'Relancer une démo'}
                </Button>
              ) : (
                <div className="text-center space-y-2">
                  <Button size="lg" variant="outline" className="w-full h-12" disabled>
                    <Clock className="mr-2 h-5 w-5" />
                    {t('demo.ended.cooldownButton') || 'Démo indisponible temporairement'}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    {t('demo.ended.cooldownMessage') || 'Prochaine démo disponible dans'}{' '}
                    <span className="font-medium text-foreground">{formatCooldown(cooldownRemaining)}</span>
                  </p>
                </div>
              )}

              {/* Back to home */}
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => router.push('/')}
              >
                {t('demo.ended.backHome') || "Retour à l'accueil"}
              </Button>
            </div>

            {/* Trust badges */}
            <div className="mt-6 pt-6 border-t border-border/50">
              <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>{t('demo.ended.trust1') || 'Paiement sécurisé'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>{t('demo.ended.trust2') || 'Annulation facile'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>{t('demo.ended.trust3') || 'Support prioritaire'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>
  );
}
